import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { BadRequestError } from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { Button } from 'src/lib/components/Button';
import { Link as FormattedLink } from 'src/lib/components/Link';
import { TextField } from 'src/lib/components/fields/TextField';
import useForm from 'src/lib/components/form/useForm';
import Container from 'src/routes/Auth/components/Container';
import { ContactFooter } from '../components/Footer';
import { z } from 'zod/mini';
import { getMobileAuthContext, getMobileAuthPayload } from '../mobileAuthContext';

interface FormValues {
    email: string;
}

export const GoogleLogin = () => {
    const { t } = useTranslation('auth.google');
    const { t: tValidation } = useTranslation('validation');

    const api = useAPI();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();

    // Pre-fill email from URL params (when redirected from login page)
    const initialEmail = searchParams.get('email') || '';

    const form = useForm<FormValues>({
        validationSchema: z.object({
            email: z.string().check(z.regex(z.regexes.email, tValidation('email'))),
        }),
        initialValues: {
            email: initialEmail,
        },
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            // External auth flows (mobile, CLI) are handled centrally:
            // 1. Login.tsx stores params in sessionStorage
            // 2. App.tsx checks sessionStorage after auth and redirects
            // This keeps login method pages simple and avoids duplication.

            try {
                const mobileContext = getMobileAuthContext(searchParams);
                const mobilePayload = getMobileAuthPayload(mobileContext) || {};

                const result = await api.post('/auth/google/check/', {
                    email: values.email,
                    ...mobilePayload,
                });

                if (result.allowed && result.redirect_url) {
                    // External auth params remain in sessionStorage through OAuth redirect
                    window.location.href = result.redirect_url;
                    return;
                }

                // Shouldn't reach here if backend returns proper error status
                enqueueSnackbar(t('googleLoginUnavailable'), { variant: 'error' });
            } catch (err: any) {
                // Show error inline for both mobile and web users
                // This allows users to retry or switch to password/SSO login
                const errorCode = err instanceof BadRequestError ? err.errorCode : undefined;
                const errorMessage = errorCode
                    ? t(`error_${errorCode}`)
                    : t('googleLoginUnavailable');
                setErrors({ email: errorMessage });
            } finally {
                setSubmitting(false);
            }
        },
    });

    const errors = form.submitCount > 0 ? form.errors : {};

    return (
        <>
            <Container width={360}>
                <form onSubmit={form.handleSubmit}>
                    <Box mb={3}>
                        <Typography variant="h6" textAlign="center">
                            {t('title')}
                        </Typography>
                    </Box>
                    <Box mb={4}>
                        <TextField
                            label={t('emailLabel')}
                            name="email"
                            type="email"
                            autoCapitalize="none"
                            autoComplete="email"
                            autoCorrect="off"
                            spellCheck={false}
                            value={form.values.email}
                            onChange={form.handleChange}
                            error={errors.email}
                            autoFocus={!initialEmail}
                        />
                    </Box>
                    <Box mb={2}>
                        <Button
                            type="submit"
                            kind="filled"
                            color="primary"
                            fullWidth
                            disabled={form.isSubmitting}
                        >
                            {t('continueWithGoogle')}
                        </Button>
                    </Box>
                    <Typography variant="body3" textAlign="center">
                        <FormattedLink to="/login">{t('loginWithPassword')}</FormattedLink>
                        {' | '}
                        <FormattedLink to="/sso">{t('loginWithSSO')}</FormattedLink>
                    </Typography>
                </form>
            </Container>
            <ContactFooter />
        </>
    );
};
